import { Directive, HostListener, Input, ElementRef } from '@angular/core';

import { Constants } from '@grid/utils/constants';
import { NumericInputOptions } from '../models';

@Directive({
  selector: '[appNumericInput]',
})
export class NumericInputDirective {
  //#region Inputs

  @Input({ required: true }) options: NumericInputOptions | undefined;

  //#endregion
  defaultDecimalSeparator: string[] = ['.'];

  constructor(private hostElement: ElementRef<HTMLInputElement>) {}

  //#region Getters

  get nativeElement(): any {
    return this.hostElement.nativeElement;
  }

  get allowDecimals(): boolean {
    return this.options?.allowDecimals ?? true;
  }

  get allowSign(): boolean {
    return this.options?.allowSign ?? true;
  }

  get decimalSeparator(): string[] {
    return this.options?.decimalSeparator ?? this.defaultDecimalSeparator;
  }

  get sign(): string {
    return '-';
  }

  get max(): number {
    return (
      (this.options?.allowPercentage
        ? Constants.NumericOptions.maxPercentage
        : this.options?.max) ?? Number.MAX_SAFE_INTEGER
    );
  }

  get min(): number {
    return (
      (this.options?.allowPercentage
        ? Constants.NumericOptions.minPercentage
        : this?.options?.min) ?? Number.MIN_SAFE_INTEGER
    );
  }

  get decimalPrecision(): number {
    return this.options?.decimalPrecision || 0;
  }

  get step(): number {
    return this?.options?.step || 1;
  }

  get autocompleteToDecimalPrecision(): boolean {
    return this?.options?.autocompleteToDecimalPrecision ?? false;
  }

  //#endregion

  @HostListener('keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    const key = e.key;
    const selectionStart = this.nativeElement.selectionStart;
    const controlOrCommand = e.ctrlKey === true || e.metaKey === true;

    const value = this.replaceSelection('');

    const escapedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Escape',
      'Tab',
      'F12',
      'Delete',
    ];

    if (
      escapedKeys.indexOf(key) !== -1 ||
      // Allow: Ctrl+A and Command+A
      (key === 'a' && controlOrCommand) ||
      // Allow: Ctrl+C and Command+C
      (key === 'c' && controlOrCommand) ||
      // Allow: Ctrl+V and Command+V
      (key === 'v' && controlOrCommand) ||
      // Allow: Ctrl+X and Command+X
      (key === 'x' && controlOrCommand)
    ) {
      return;
    }

    if (key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopImmediatePropagation();

      const step = e.key === 'ArrowUp' ? +this.step : -this.step;

      const result = this.stepIncrement(value, step);

      this.updateInputValue(result);
    }

    if (key === '+') {
      e.stopPropagation();
    }

    if (!this.isKeyValid(key, value, selectionStart)) {
      e.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: any): void {
    event.preventDefault();

    const clipboardData = event.clipboardData.getData('text/plain');
    let position = this.nativeElement.selectionStart;

    let value = this.replaceSelection('');

    const clipboardDataIterator = clipboardData[Symbol.iterator]();

    for (const key of clipboardDataIterator) {
      if (this.isKeyValid(key, value, position)) {
        value = this.insert(value, key, position);
        position++;
      }
    }

    this.updateInputValue(value);
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();

    const dataTransfer = event.dataTransfer?.getData('text');

    if (dataTransfer == null || dataTransfer.trim() === '') {
      return;
    }

    let position = this.nativeElement.selectionStart;

    let value = this.replaceSelection('');

    const dataTransferIterator = dataTransfer[Symbol.iterator]();

    for (const key of dataTransferIterator) {
      if (this.isKeyValid(key, value, position)) {
        value = this.insert(value, key, position);
        position++;
      }
    }

    this.updateInputValue(value);
  }

  @HostListener('wheel', ['$event'])
  onScroll(e: any): void {
    e.preventDefault();
    e.stopImmediatePropagation();

    const value = this.nativeElement.value;
    const step = -e.deltaY > 0 ? +this.step : -this.step;

    const result = this.stepIncrement(value, step);

    this.updateInputValue(result);
  }

  @HostListener('blur')
  onBlur(): void {
    const value = this.beautifyValue(this.nativeElement.value);

    this.updateInputValue(value);
  }

  //#region Private methods

  private updateInputValue(value: string): void {
    this.nativeElement.value = value;
    this.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * @description check if the given key can be added to the value
   * without compromising the number or the restrictions
   */
  private isKeyValid(key: string, value: string, position: number): boolean {
    if (key === this.sign) {
      return this.canAddSign(value, position);
    }

    if (this.decimalSeparator.includes(key)) {
      return this.canAddDecimalSeparator(value, position);
    }

    if (!new RegExp('^[0-9]*$').test(key)) {
      return false;
    }

    return this.canAddDigit(value, key, position);
  }

  /**
   * @description Increments or decrements the given value
   * we must know the current decimal precision in order to call toFixed with the same precision
   * https://stackoverflow.com/questions/12511057/float-sum-with-javascript
   */
  private stepIncrement(value: string, step: number): string {
    const precision = Math.max(
      this.getDecimalsCount(value),
      this.getDecimalsCount(
        String(step).includes('e-')
          ? String(step.toFixed(this.computePrecision(String(step))))
          : String(step)
      )
    );

    const result = Number(value) + step;

    if (result < this.min || result > this.max) {
      return value;
    }

    return result.toFixed(precision);
  }

  /**
   * @description Checks if the configured sign can be added to the given value, at the given position
   */
  private canAddSign(value: string, position?: number): boolean {
    // sign must be allowed
    if (!this.allowSign) {
      return false;
    }

    // sign most not already by included in the value
    if (value.includes(this.sign)) {
      return false;
    }

    // cursor must be at the beginning of string
    if (position !== 0) {
      return false;
    }

    // negative values must be allowed
    if (this.min >= 0) {
      return false;
    }

    // check that the min value is not exceeded
    if (value && Number(this.sign + value) < this.min) {
      return false;
    }

    return true;
  }

  /**
   * @description Checks if the given digit set can be added to the given value, at the given position
   */
  private canAddDigit(value: string, digit: string, position: number): boolean {
    const result = this.insert(value, digit, position);

    // the check for min < 0 and max > 0 was added to allow user to enter data
    // if the initial value is smaller/bigger than the current min/max
    // example: initial value = 1 and min_value = 120

    // resulting number must not be lower than given values
    if (this.min != null && this.min < 0 && Number(result) < this.min) {
      return false;
    }

    // resulting number must not be greater than given values
    if (this.max != null && this.max >= 0 && Number(result) > this.max) {
      return false;
    }

    // decimals must not exceed given decimal precisions
    if (this.isDecimal(value, position) && !this.canAddDecimal(value)) {
      return false;
    }

    return true;
  }

  /**
   * @description Checks if a new decimal can be added in the given number
   */
  private canAddDecimal(value: string): boolean {
    if (!this.allowDecimals || !this.decimalPrecision) {
      return false;
    }

    // Find the first decimal separator in the value
    const separatorIndex =
      this.decimalSeparator
        .map((separator) => value.indexOf(separator))
        .find((index) => index !== -1) ?? -1;

    // If no decimal separator found, decimal can be added
    if (separatorIndex === -1) {
      return true;
    }

    // Check if current decimal part exceeds precision
    const decimalPart = value.substring(separatorIndex + 1);

    return decimalPart.length < this.decimalPrecision;
  }

  /**
   * @description Checks if a digit inserted at the given position would be a decimal
   */
  private isDecimal(value: string, position: number): boolean {
    const separatorIndex =
      this.decimalSeparator
        .map((separator) => value.indexOf(separator))
        .find((index) => index !== -1) ?? -1;

    return separatorIndex !== -1 && position > separatorIndex;
  }

  /**
   * @description Checks if the decimal separator can be added at the given position
   */
  private canAddDecimalSeparator(value: string, position: number): boolean {
    // separator must be allowed
    if (!this.allowDecimals || !this.decimalPrecision) {
      return false;
    }

    // Check if any decimal separator already exists
    const hasDecimalSeparator = this.decimalSeparator.some((separator) =>
      value.includes(separator)
    );
    if (hasDecimalSeparator) {
      return false;
    }

    // Check if inserting a decimal separator would result in a value below the minimum threshold
    const exceedsMinValue = this.decimalSeparator.some((separator) => {
      const result = this.insert(value || '0', separator, position);
      return Number(result) < this.min;
    });

    return !exceedsMinValue;
  }

  /**
   * @description Adds the given value to the source at the given position
   */
  private insert(source: string, value: string, position: number): string {
    return source.substring(0, position) + value + source.substring(position);
  }

  /**
   * @description Replaces the selected text of the input with the given value
   */
  private replaceSelection(value: string): string {
    const originalValue = this.nativeElement.value;
    const selectionStart: number = this.nativeElement.selectionStart;
    const selectionEnd: number = this.nativeElement.selectionEnd;

    const result =
      originalValue.slice(0, selectionStart) +
      (value || '') +
      originalValue.slice(selectionEnd, originalValue.length);

    return result;
  }

  /**
   * @description Adds missing leading digits or 0s to complete the precision
   */
  private beautifyValue(value: string): string {
    if (!value) {
      return value;
    }

    const precision = this.getDecimalsCount(value);

    const result = Number(value);

    if (isNaN(result)) {
      return '';
    }

    if (this.autocompleteToDecimalPrecision) {
      return result.toFixed(this.decimalPrecision);
    }

    return result.toFixed(precision);
  }

  /**
   * @description Counts the number of decimals in the give value
   */
  private getDecimalsCount(value: string): number {
    if (!value) {
      return 0;
    }

    // Find the first decimal separator that exists in the value
    const separator = this.decimalSeparator.find((sep) => value.includes(sep));

    if (!separator) {
      return 0;
    }

    const parts = value.split(separator);
    return parts.length > 1 ? parts[1].length : 0;
  }

  private computePrecision(value: string): number {
    return +value.split('e-')[1];
  }
  //#endregion
}
